import { useSelector } from 'react-redux';
import { groupBy, keyBy, pick, values } from 'lodash';
import { Card, Flex, Text } from '@chakra-ui/react';
import Modal from '../../modal/Modal';
import useForm from '../../form/Form';
import { TState, useActions } from '../../../redux/store';
import useRequiredValidation from '../../form/useRequiredValidation';
import { addStats, deleteStat, updateStats } from '../../../api/tabsApi';
import { useCanI } from '../../../redux/actions';

const ManageStats = () => {
  const requiredValidation = useRequiredValidation();
  const canI = useCanI();

  const dataInputs = useSelector((state: TState) => state.dataInputs);
  const userList = useSelector((state: TState) => state.userList);
  const modalType = useSelector((state: TState) => state.modalType);
  const rowData = useSelector((state: TState) => state.rowData);
  const modalData = useSelector((state: TState) => state.modalData);

  const inputsByName = keyBy(dataInputs, 'name_id');

  const { setModalType, onClose } = useActions();

  const inputs = [
    {
      label: 'Titre',
      name_id: 'title',
      type: 'text',
      required: true,
      container_id: 0,
    },
    {
      label: 'Type de graphique',
      name_id: 'type',
      type: 'select',
      options: [
        { label: 'Comparer passé/présent', value: 'compare' },
        { label: 'Analyse de données', value: 'analytic' },
      ],
      required: true,
      container_id: 0,
    },
    {
      label: 'Période de temps à comparer',
      name_id: 'duration',
      type: 'select',
      options: [
        // { label: 'Dynamique', value: 'dynamic' },
        { label: 'Semaine', value: 'week' },
        { label: 'Mois', value: 'month' },
        { label: 'Année', value: 'year' },
      ],
      required: true,
      container_id: 0,
    },
    {
      label: '',
      labelFn: (fd: any) =>
        `Afficher le detail dans le temps${
          /week|month/.test(fd.duration)
            ? ' (par jour)'
            : /year/.test(fd.duration)
              ? ' (par mois)'
              : ''
        }`,
      name_id: 'detail',
      type: 'switch',
      container_id: 0,
      condition: () => false, // @todo activate when dynamic option is available
    },
    {
      label: 'Stat sur les actions des utillisateurs',
      name_id: 'isUser',
      type: 'switch',
      container_id: 1,
    },
    {
      label: 'Utilisateurs concernés',
      name_id: 'users',
      type: 'multiselect',
      options: userList.map((user) => ({
        label: user.name,
        value: user._id,
        color: user.userColor,
      })) as any,
      condition: (data: any) => data.isUser,
      required: true,
      container_id: 1,
    },
    {
      label: 'Données à comparer (max 5)',
      name_id: 'dataToCompare',
      type: 'multiselect',
      max: 5,
      options: dataInputs
        ?.filter((i) => /select|switch|number|counter/.test(i.type))
        ?.map((input) => ({
          label: `${input.label ? `${input.label} / ` : ''}${input.name_id} / ${
            input.type
          }`,
          value: input.name_id,
        })) as any,
      container_id: 2,
    },
    {
      label: 'Critères d’analyse',
      name_id: 'filtersToApply',
      type: 'multiselect',
      options: dataInputs
        ?.filter((i) => /users|select|switch/.test(i.type))
        ?.map((input) => ({
          label: `${input.label ? `${input.label} / ` : ''}${input.name_id} / ${
            input.type
          }`,
          value: input.name_id,
        })) as any,
      container_id: 3,
    },
  ];

  const { Form, formData, setErrors, setFormData } = useForm(inputs);

  const inputsDetail = Array.from({
    length: formData.dataToCompare?.length,
  }).map((_, idx) => ({
    label: `Valeur de ${
      inputsByName[formData.dataToCompare?.[idx]]?.label ||
      formData.dataToCompare?.[idx]
    } à analyser`,
    name_id: inputsByName[formData.dataToCompare?.[idx]]?.name_id,
    type: 'multiselect',
    container_id: 2,
    options: inputsByName[formData.dataToCompare?.[idx]]?.options?.map(
      (option, idx) => ({ label: option, value: idx }),
    ),
    required: true,
    condition: () =>
      /select/.test(inputsByName[formData.dataToCompare?.[idx]]?.type),
  }));

  const filtersDetail = Array.from({
    length: formData.filtersToApply?.length,
  }).map((_, idx) => ({
    label: `Valeur de ${
      inputsByName[formData.filtersToApply?.[idx]]?.label ||
      formData.filtersToApply?.[idx]
    } à filtrer`,
    name_id: inputsByName[formData.filtersToApply?.[idx]]?.name_id,
    type: /users|select/.test(
      inputsByName[formData.filtersToApply?.[idx]]?.type,
    )
      ? 'multiselect'
      : /number/.test(inputsByName[formData.filtersToApply?.[idx]]?.type)
        ? 'number_range'
        : 'switch',
    container_id: 3,
    options: inputsByName[formData.filtersToApply?.[idx]]?.options?.map(
      (option, idx) => ({ label: option, value: idx }),
    ),
    required: !/switch/.test(
      inputsByName[formData.filtersToApply?.[idx]]?.type,
    ),
  }));

  const {
    Form: Form2,
    formData: formData2,
    setErrors: setErrors2,
    setFormData: setFormData2,
  } = useForm([...inputsDetail, ...filtersDetail]);

  const handleSubmit = async () => {
    const isValid =
      requiredValidation(inputs, formData, setErrors) &&
      requiredValidation(inputsDetail, formData2, setErrors2);

    if (!isValid) return;

    const keyIds = inputs.map((i) => i.name_id);
    const optionsIds = inputsDetail.map((i) => i.name_id!);
    const filtersIds = filtersDetail.map((i) => i.name_id!);

    const datas = pick(formData, keyIds);
    const options = pick(formData2, optionsIds);
    const filtersOptions = pick(formData2, filtersIds);

    const data: any = {
      ...datas,
      options,
      filtersOptions,
    };

    if (modalType === 'addStats') {
      await addStats(data);
      setModalType('editStats');

      setTimeout(() => {
        setFormData(datas);
        setFormData2({
          ...data.options,
          ...data.filtersOptions,
        });
      }, 1);
    } else {
      await updateStats(formData._id, data);
    }

    modalData.refreshData?.();
  };

  const handleDeleteStat = async () => {
    await deleteStat(rowData._id);
    modalData.refreshData?.();
    onClose();
  };

  const groupedForm = groupBy(
    [...Form, ...Form2],
    (f: any) => f.props?.field?.container_id,
  );

  const renderForm = (
    <Flex gap={3} direction='column'>
      {values(groupedForm).map((fields) => (
        <Card p={3} pt={1} bgColor='#f9fafb'>
          {fields}
        </Card>
      ))}
    </Flex>
  );

  const deletePermission = canI('stats', 'delete');

  const modalOptions = {
    addStats: {
      component: renderForm,
      title: 'Ajouter une stat',
      action: handleSubmit,
      actionLabel: 'Ajouter',
    },
    editStats: {
      component: renderForm,
      title: 'Editer les stats',
      action: handleSubmit,
      actionLabel: 'Sauvegarder',
      canDelete: deletePermission ? 'deleteStats' : '',
    },
    deleteStats: {
      component: <Text>Confirmer la suppression ?</Text>,
      action: handleDeleteStat,
      title: 'Supprimer la stat',
      actionLabel: 'Confirmer',
    },
  };

  return <Modal options={modalOptions} />;
};

export default ManageStats;
